import { computed, effect, inject } from '@angular/core';
import { withCallStatus } from '@ngrx-traits/signals';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  User,
  WhatsAppCompleteRegistrationCredentials,
  WhatsAppInitiateCredentials,
  WhatsAppVerifyCredentials,
} from '@seatix/shared-domain-auth';
import { injectLocalStorage } from 'ngxtension/inject-local-storage';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import {
  isWhatsAppDeliveryFailedError,
  mapWhatsAppOtpError,
} from '../auth-api/whatsapp-otp-error';

// Los símbolos `WhatsApp*` que quedan aquí nombran la ruta HTTP
// `/api/auth/whatsapp/*`, no el canal de entrega: describen el contrato del
// transporte y se renombran cuando se renombre la ruta en el backend. Lo que
// esta feature expone —estado, flag y métodos— sí es agnóstico del canal,
// porque el OTP sale por WhatsApp con SMS de respaldo.

const COUNTDOWN_DURATION_SECONDS = 60;

export function withPhoneAuth() {
  return signalStoreFeature(
    // Depende de la sesión emitida por `withAuthentication`, que debe
    // componerse antes en el store.
    type<{
      methods: {
        setUser: (user: User) => void;
        shouldNavigate: () => void;
        resetNavigationFlag: () => void;
      };
    }>(),
    withState<{
      phoneVerificationToken: string | null;
      phoneSignature: string | null;
      phoneIsNewUser: boolean;
      phoneInitiateIsDeliveryFailed: boolean;
    }>({
      phoneVerificationToken: null,
      phoneSignature: null,
      phoneIsNewUser: false,
      phoneInitiateIsDeliveryFailed: false,
    }),
    withCallStatus({
      prop: 'phoneInitiate',
      errorType: 'string',
    }),
    withCallStatus({
      prop: 'phoneVerify',
      errorType: 'string',
    }),
    withCallStatus({
      prop: 'phoneCompleteRegistration',
      errorType: 'string',
    }),
    withProps(() => ({
      // La clave es estado persistido en el navegador de usuarios reales:
      // cambiarla pierde los countdowns activos y los manda a un 429 del
      // proveedor de OTP. Se queda con el nombre con el que se escribió.
      _phoneCountdownStorage: injectLocalStorage<number | null>(
        'whatsapp-otp-countdown',
        {
          defaultValue: null,
        },
      ),
    })),
    withComputed((store) => {
      const isPhoneCountdownActive = computed(() => {
        const endTime = store._phoneCountdownStorage();
        return endTime ? Date.now() < endTime : false;
      });

      const phoneCountdownEndTime = computed(() =>
        store._phoneCountdownStorage(),
      );

      return {
        isPhoneCountdownActive,
        phoneCountdownEndTime,
      };
    }),
    withMethods((store) => {
      const authApiService = inject(AuthApiService);

      function startPhoneCountdown() {
        const endTime = Date.now() + COUNTDOWN_DURATION_SECONDS * 1000;
        store._phoneCountdownStorage.set(endTime);
      }

      function clearPhoneCountdown() {
        store._phoneCountdownStorage.set(null);
      }

      // Limpia el estado de un flujo previo (call-status) para que un diálogo
      // nuevo arranque siempre en la captura de teléfono. Sin esto, el slice
      // `initiate` queda en `loaded` de la sesión anterior y el diálogo salta
      // directo a la pantalla de OTP sin volver a pedir el número. El countdown
      // de reenvío NO se borra aquí: persiste hasta expirar o hasta un verify
      // exitoso, para respetar el cooldown de 60s entre reaperturas (espejo de
      // `with-email-verification`).
      function resetPhoneState() {
        patchState(store, {
          phoneInitiateCallStatus: 'init',
          phoneVerifyCallStatus: 'init',
          phoneCompleteRegistrationCallStatus: 'init',
          phoneVerificationToken: null,
          phoneSignature: null,
          phoneIsNewUser: false,
          phoneInitiateIsDeliveryFailed: false,
        });
      }

      return {
        startPhoneCountdown,
        clearPhoneCountdown,
        resetPhoneState,

        initiatePhone: rxMethod<{
          credentials: WhatsAppInitiateCredentials;
          turnstileToken: string;
        }>(
          pipe(
            switchMap(({ credentials, turnstileToken }) => {
              // Descarta cualquier error de verify previo: tras un reenvío el
              // código anterior ya no aplica, y `errorMessage` lo seguiría
              // mostrando como inválido.
              patchState(store, {
                phoneVerifyCallStatus: 'init',
                phoneInitiateIsDeliveryFailed: false,
              });
              store.setPhoneInitiateLoading();

              return authApiService
                .initiateWhatsAppOtp(credentials, turnstileToken)
                .pipe(
                  tap(() => {
                    store.setPhoneInitiateLoaded();
                    startPhoneCountdown();
                  }),
                  catchError((error) => {
                    store.setPhoneInitiateError(mapWhatsAppOtpError(error));
                    patchState(store, {
                      phoneInitiateIsDeliveryFailed:
                        isWhatsAppDeliveryFailedError(error),
                    });
                    return EMPTY;
                  }),
                );
            }),
          ),
        ),

        verifyPhone: rxMethod<WhatsAppVerifyCredentials>(
          pipe(
            switchMap((credentials) => {
              store.setPhoneVerifyLoading();

              return authApiService.verifyWhatsAppOtp(credentials).pipe(
                tap((response) => {
                  clearPhoneCountdown();

                  // El backend envuelve la respuesta en `ApiResponse` ({ data }).
                  const result = response.data;

                  if (result.is_new_user) {
                    // Teléfono nuevo: no hay sesión todavía. Guardamos el token
                    // y la firma (opaca) para completar el registro, y marcamos
                    // el flujo como nuevo usuario para que el diálogo muestre la
                    // pantalla de datos. `phone_signature` se reenvía tal cual
                    // en complete-registration.
                    patchState(store, {
                      phoneVerificationToken: result.phone_verification_token,
                      phoneSignature: result.phone_signature,
                      phoneIsNewUser: true,
                    });
                    store.setPhoneVerifyLoaded();
                    return;
                  }

                  // Usuario existente: el backend ya emitió la sesión web y el
                  // usuario viene en `data`.
                  store.shouldNavigate();
                  store.setUser(result);
                  store.setPhoneVerifyLoaded();
                }),
                catchError((error) => {
                  store.setPhoneVerifyError(mapWhatsAppOtpError(error));
                  return EMPTY;
                }),
              );
            }),
          ),
        ),

        completeRegistration: rxMethod<WhatsAppCompleteRegistrationCredentials>(
          pipe(
            switchMap((credentials) => {
              store.setPhoneCompleteRegistrationLoading();

              return authApiService
                .completeWhatsAppRegistration(credentials)
                .pipe(
                  tap((response) => {
                    store.shouldNavigate();
                    store.setUser(response.data);
                    store.setPhoneCompleteRegistrationLoaded();
                  }),
                  catchError((error) => {
                    store.setPhoneCompleteRegistrationError(
                      mapWhatsAppOtpError(error),
                    );
                    return EMPTY;
                  }),
                );
            }),
          ),
        ),
      };
    }),
    withHooks((store) => ({
      onInit: () => {
        effect(() => {
          const endTime = store._phoneCountdownStorage();
          const isActive = store.isPhoneCountdownActive();

          if (endTime && !isActive) {
            store._phoneCountdownStorage.set(null);
          }
        });
      },
    })),
  );
}
